import { ChevronRightIcon, GlobeEuropeAfricaIcon } from '@heroicons/react/24/outline'
import Skeleton from 'react-loading-skeleton'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'urql'
import { classNames } from '../../_helpers'
import { readSeasonSectionsOverviewQuery } from '../../_lib/graphql/queries'
import { Button } from '../../_ui/Button'
import { MainHeader } from '../../_ui/MainHeader'

type PropTypes = {
  backButton?: boolean
  baseLink: string
}

export const SeasonSectionsOverview = ({ backButton, baseLink }: PropTypes) => {
  const navigate = useNavigate()
  const [seasonResult] = useQuery({
    query: readSeasonSectionsOverviewQuery
  })

  const sections = seasonResult.data?.readCurrentSeason?.sections || []
  const totalSectionSlots = sections.reduce((acc, section) => {
    return acc + (section?.sectionRoles || []).reduce((roleAcc, role) => roleAcc + (role?.totalSectionSlots || 0), 0)
  }, 0)
  const totalAcceptedSectionSlots = sections.reduce((acc, section) => {
    return acc + (section?.sectionRoles || []).reduce((roleAcc, role) => roleAcc + (role?.totalAcceptedSectionSlots || 0), 0)
  }, 0)
  const totalPotentialSectionSlotsToConfirm = sections.reduce((acc, section) => {
    return acc + (section?.sectionRoles || []).reduce((roleAcc, role) => roleAcc + (role?.totalPotentialSectionSlotsToConfirm || 0), 0)
  }, 0)
  const totalOpenSectionSlots = Math.max(totalSectionSlots - totalAcceptedSectionSlots, 0)
  const totalCoveragePercentage = totalSectionSlots > 0
    ? Math.round((totalAcceptedSectionSlots / totalSectionSlots) * 100)
    : 0

  const sectionStats = sections
    .map(section => {
      const totalSlots = (section?.sectionRoles || []).reduce((acc, role) => acc + (role?.totalSectionSlots || 0), 0)
      const acceptedSlots = (section?.sectionRoles || []).reduce((acc, role) => acc + (role?.totalAcceptedSectionSlots || 0), 0)
      const potentialSlots = (section?.sectionRoles || []).reduce((acc, role) => acc + (role?.totalPotentialSectionSlotsToConfirm || 0), 0)
      const openSlots = Math.max(totalSlots - acceptedSlots, 0)
      const hasSlots = totalSlots > 0
      const coverage = hasSlots ? Math.round((acceptedSlots / totalSlots) * 100) : 0
      const potentialCoverage = hasSlots ? Math.round((potentialSlots / totalSlots) * 100) : 0

      return {
        section,
        totalSlots,
        acceptedSlots,
        potentialSlots,
        openSlots,
        hasSlots,
        coverage,
        potentialCoverage,
      }
    })
    .sort((a, b) => {
      if (a.hasSlots !== b.hasSlots) {
        return a.hasSlots ? -1 : 1
      }

      return a.coverage - b.coverage
    })

  return (
    <div className={'flex flex-col gap-8'}>
      {backButton && (
        <div>
          <Button onClick={() => navigate('../', {relative: 'path'})}>
            Zurück
          </Button>
        </div>
      )}

      <MainHeader
        title={'Schichtplanung'}
        subtitle={seasonResult.fetching
          ? 'Die Bereiche werden geladen.'
          : `Aktuelle Saison${seasonResult.data?.readCurrentSeason?.title ? `: ${seasonResult.data.readCurrentSeason.title}` : ''}. Wähle links einen Bereich aus oder öffne unten direkt die Detailansicht.`
        }
      />

      <div className={'grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr_1fr]'}>
        {seasonResult.fetching ? (
          <>
            <div className={'rounded-3xl bg-gradient-to-br from-primary-500 via-primary-400 to-[#0b87d8] p-6 text-white shadow-lg ring-1 ring-black/10 xl:col-span-1'}>
              <Skeleton width={160} height={18} baseColor={'rgba(255,255,255,0.25)'} highlightColor={'rgba(255,255,255,0.4)'} />
              <div className={'mt-4'}>
                <Skeleton width={'55%'} height={54} baseColor={'rgba(255,255,255,0.25)'} highlightColor={'rgba(255,255,255,0.4)'} />
              </div>
              <div className={'mt-4'}>
                <Skeleton width={'85%'} height={18} baseColor={'rgba(255,255,255,0.25)'} highlightColor={'rgba(255,255,255,0.4)'} />
              </div>
            </div>

            <div className={'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5'}>
              <Skeleton width={120} height={18} />
              <div className={'mt-3'}>
                <Skeleton width={'70%'} height={28} />
              </div>
              <div className={'mt-2'}>
                <Skeleton width={'85%'} height={14} />
              </div>
            </div>

            <div className={'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5'}>
              <Skeleton width={120} height={18} />
              <div className={'mt-3'}>
                <Skeleton width={'70%'} height={28} />
              </div>
              <div className={'mt-2'}>
                <Skeleton width={'85%'} height={14} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={'rounded-3xl bg-gradient-to-br from-primary-500 via-primary-400 to-[#0b87d8] p-6 text-white shadow-lg ring-1 ring-black/10'}>
              <p className={'text-sm font-semibold uppercase tracking-wide text-white/75'}>Abdeckung gesamt</p>
              <div className={'mt-4 flex items-end gap-4'}>
                <p className={'text-5xl font-black leading-none'}>{totalCoveragePercentage}%</p>
                <p className={'pb-1 text-sm text-white/75'}>
                  {totalAcceptedSectionSlots} von {totalSectionSlots} Aufgaben belegt
                </p>
              </div>

              <div className={'mt-5'}>
                <div className={'mb-2 flex items-center justify-between text-sm text-white/80'}>
                  <span>Offen</span>
                  <span>{totalOpenSectionSlots} Aufgaben</span>
                </div>
                <div className={'h-3 rounded-full bg-white/20 overflow-hidden'}>
                  <div
                    className={'h-full rounded-full bg-white transition-all'}
                    style={{ width: `${totalCoveragePercentage}%` }}
                  />
                </div>
              </div>

              <p className={'mt-4 text-sm text-white/80'}>
                {totalPotentialSectionSlotsToConfirm > 0
                  ? `${totalPotentialSectionSlotsToConfirm} Aufgaben sind potenziell belegbar.`
                  : 'Hier siehst du sofort, wo noch Aufgaben offen sind.'}
              </p>
            </div>

            <div className={'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5'}>
              <div>
                <p className={'text-sm font-medium text-gray-500'}>Aufgaben gesamt</p>
                <p className={'mt-2 text-4xl font-semibold text-gray-900'}>{totalSectionSlots}</p>
              </div>
              <div className={'mt-2 text-sm text-gray-600'}>
                Alle Aufgaben in allen Bereichen.
                <div className={'font-medium text-sky-600'}>
                  {totalPotentialSectionSlotsToConfirm} davon potenziell belegbar
                </div>
              </div>
            </div>

            <div className={'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5'}>
              <p className={'text-sm font-medium text-gray-500'}>Aufgaben offen</p>
              <p className={'mt-2 text-4xl font-semibold text-gray-900'}>{totalOpenSectionSlots}</p>
              <p className={'mt-2 text-sm text-gray-600'}>
                Noch nicht bestätigte Aufgaben.
              </p>
            </div>
          </>
        )}
      </div>

      <div>
        <div className={'mb-4'}>
          <h2 className={'text-base font-semibold text-gray-900'}>Bereiche</h2>
          <p className={'mt-1 text-sm text-gray-600'}>
            Jede Kachel führt direkt in die Schichtansicht des jeweiligen Bereichs.
          </p>
        </div>

        {seasonResult.fetching ? (
          <div className={'season-sections-overview-grid'}>
            <div className={'season-sections-overview-cards'}>
              {new Array(4).fill(null).map((_, index) => (
                <div key={index} className={'rounded-3xl bg-white p-5 shadow-sm ring-1 ring-black/5'}>
                  <Skeleton width={'55%'} height={22} />
                  <div className={'mt-3'}>
                    <Skeleton count={2} />
                  </div>
                  <div className={'mt-4 flex gap-2'}>
                    <Skeleton width={96} height={28} />
                    <Skeleton width={112} height={28} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : sectionStats.length > 0 ? (
          <div className={'season-sections-overview-grid'}>
            <div className={'season-sections-overview-cards'}>
            {sectionStats.map(({ section, totalSlots, acceptedSlots, potentialSlots, openSlots, hasSlots, coverage, potentialCoverage }) => (
              <Link
                key={section?.id || ''}
                to={`${baseLink}${section?.id || ''}/`}
                className={classNames(
                  'group rounded-3xl p-6 shadow-sm ring-1 transition hover:shadow-md',
                  hasSlots
                    ? 'bg-white ring-black/5'
                    : 'bg-amber-50 ring-amber-200 border border-dashed border-amber-300',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                )}
              >
                <div className={'flex items-start justify-between gap-4'}>
                  <div className={'min-w-0'}>
                    <p className={classNames(
                      'text-lg font-semibold group-hover:text-primary-600',
                      hasSlots ? 'text-gray-900' : 'text-amber-900'
                    )}>
                      {section?.title || 'Ohne Titel'}
                    </p>
                    {hasSlots ? (
                      <div className={'mt-1 text-sm text-gray-600'}>
                        {acceptedSlots} von {totalSlots} Aufgaben belegt
                          <div className={`font-medium ${potentialSlots > 0 ? 'text-sky-600' : 'text-gray-400'}`}>
                        {potentialSlots > 0 ? (
                            `${potentialSlots} potenziell belegbar`
                        ) : ` Keine Aufgaben belegbar`}
                          </div>
                      </div>
                    ) : (
                      <p className={'mt-1 text-sm text-amber-800'}>
                        Dieser Bereich hat noch keine Schichten. Hier solltest du zuerst Aufgaben anlegen.
                      </p>
                    )}
                  </div>

                  <div className={'shrink-0 text-right'}>
                    {hasSlots ? (
                      <>
                        <p className={classNames(
                          'text-3xl font-black leading-none',
                          coverage >= 90 ? 'text-emerald-600' : coverage >= 70 ? 'text-amber-600' : 'text-rose-600'
                        )}>
                          {coverage}%
                        </p>
                        <p className={'mt-1 text-xs font-medium text-gray-500'}>
                          {openSlots > 0 ? `${openSlots} offen` : 'voll belegt'}
                        </p>
                      </>
                    ) : (
                      <p className={'text-sm font-semibold uppercase tracking-wide text-amber-700'}>
                        Keine Schichten
                      </p>
                    )}
                  </div>
                </div>

                {hasSlots ? (
                  <>
                    <div className={'mt-5 h-3 overflow-hidden rounded-full bg-gray-100'}>
                      <div
                        className={classNames(
                          'h-full rounded-full transition-all',
                          coverage >= 90 ? 'bg-emerald-500' : coverage >= 70 ? 'bg-amber-500' : 'bg-rose-500'
                        )}
                        style={{ width: `${coverage}%` }}
                      />
                    </div>

                    <div className={'mt-3'}>
                      <div className={'mb-1 flex items-center justify-between text-xs font-medium text-gray-500'}>
                        <span>Verfügbarkeiten</span>
                        <span>{potentialCoverage}% potenziell belegbar</span>
                      </div>
                      <div className={'h-2 overflow-hidden rounded-full bg-gray-100'}>
                        <div
                          className={classNames(
                            'h-full rounded-full transition-all',
                            potentialCoverage >= 90 ? 'bg-sky-500' : potentialCoverage >= 70 ? 'bg-sky-400' : 'bg-sky-300'
                          )}
                          style={{ width: `${potentialCoverage}%` }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={'mt-5 rounded-2xl border border-amber-200 bg-amber-100/70 p-4 text-sm text-amber-900'}>
                    Ohne Schichten kann keine Belegung stattfinden. Lege zuerst Schichten an, damit Verfügbarkeiten sichtbar werden.
                  </div>
                )}

                <div className={'mt-4 flex flex-wrap gap-2'}>
                  {!hasSlots && (
                    <span className={'inline-flex items-center gap-1 rounded-full bg-amber-200 px-3 py-1 text-xs font-medium text-amber-900'}>
                      Keine Schichten
                    </span>
                  )}
                  {section?.publicAccess && (
                    <span className={'inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700'}>
                      <GlobeEuropeAfricaIcon className={'h-4 w-4'} />
                      Öffentlich zugänglich
                    </span>
                  )}
                </div>

                <div className={'mt-5 flex items-center justify-end gap-2 text-sm font-medium text-primary-600'}>
                  <span>{hasSlots ? 'Bereich öffnen' : 'Schichten anlegen'}</span>
                  <ChevronRightIcon className={'h-4 w-4 transition group-hover:translate-x-0.5'} />
                </div>
              </Link>
            ))}
            </div>
          </div>
        ) : (
          <div className={'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5'}>
            <p className={'text-sm font-medium text-gray-900'}>Keine Bereiche vorhanden</p>
            <p className={'mt-2 text-sm text-gray-600'}>
              Für die aktuelle Saison sind noch keine Bereiche hinterlegt.
            </p>
          </div>
        )}
      </div>

      <div className={'rounded-2xl bg-gray-50 p-6 ring-1 ring-black/5'}>
        <p className={'text-sm font-semibold uppercase tracking-wide text-primary-500'}>Hinweis</p>
        <p className={'mt-2 text-sm text-gray-700'}>
          Die Übersicht dient als Einstieg in die Bereichsplanung. Links findest du dieselben Bereiche zusätzlich als Navigation.
        </p>
      </div>
    </div>
  )
}
